import React from 'react';
import { Input as AntInput, InputProps as AntInputProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

const { TextArea, Password, Search } = AntInput;

export type InputVariant = 'default' | 'secondary' | 'inverse';

export interface InputProps extends AntInputProps {
  variant?: InputVariant;
  className?: string;
  status?: 'error' | 'warning';
}

type InputStyleType = 'input' | 'textArea' | 'password' | 'search';

interface InputStylesParams {
  variant?: InputVariant;
  status?: 'error' | 'warning';
  className?: string;
  type?: InputStyleType;
  disabled?: boolean;
}

const useStyles = createUseStyles((theme: ThemeType) => {
  const createDefaultInputStyles = () => {
    return {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      color: `${theme.color.semantic.text.primary} !important`,
      borderColor: theme.color.semantic.border.primary,
      borderWidth: theme.border.width.sm,
      borderRadius: theme.radius.sm,
      fontWeight: theme.typography.fontWeight.regular,
      height: '32px',
      lineHeight: theme.typography.lineHeight.normal,
      padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
      fontSize: theme.typography.fontSize.sm,
      boxShadow: 'none !important',
      '& input': {
        backgroundColor: 'transparent !important',
        border: 'none',
        outline: 'none',
        boxShadow: 'none !important',
        fontSize: theme.typography.fontSize.sm,
        color: `${theme.color.semantic.text.primary} !important`,
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
      },
      '& textarea': {
        backgroundColor: 'transparent !important',
        border: 'none',
        outline: 'none',
        boxShadow: 'none !important',
        color: `${theme.color.semantic.text.primary} !important`,
      },
      '&::placeholder': {
        color: theme.color.semantic.text.soft,
      },
      '&:hover': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.brand.default.soft,
        boxShadow: 'none !important',
      },
      '&:focus, &:active': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.brand.default.moderate,
        boxShadow: 'none !important',
      },
      '&:disabled': {
        backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        opacity: `${theme.opacity[70]} !important`,
        color: `${theme.color.semantic.text.soft} !important`,
        borderColor: `${theme.color.semantic.border.primary} !important`,
        cursor: 'not-allowed !important',
        boxShadow: 'none !important',
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
        '&:hover, &:focus, &:active': {
          backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
          borderColor: `${theme.color.semantic.border.primary} !important`,
          boxShadow: 'none !important',
        },
      },
      '&.input-status-error': {
        borderColor: theme.color.semantic.brand.danger.light,
        '&:focus': {
          borderColor: theme.color.semantic.brand.danger.light,
        },
      },
      '&.input-status-warning': {
        borderColor: theme.color.semantic.accent.orange.strong,
        '&:focus': {
          borderColor: theme.color.semantic.accent.orange.strong,
        },
      },
      '& .ant-input-prefix, & .ant-input-suffix': {
        color: theme.color.semantic.text.primary,
        '& .anticon': {
          color: theme.color.semantic.text.primary,
        },
      },
      '&:disabled .ant-input-prefix, &:disabled .ant-input-suffix': {
        color: theme.color.semantic.text.soft,
        '& .anticon': {
          color: theme.color.semantic.text.soft,
        },
      },
      '&.ant-input-affix-wrapper': {
        height: '32px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
        borderColor: theme.color.semantic.border.primary,
        boxShadow: 'none !important',
        '&:hover': {
          backgroundColor: theme.color.semantic.background.neutral.secondary,
          borderColor: theme.color.semantic.brand.default.soft,
          boxShadow: 'none !important',
        },
        '&:focus, &.ant-input-affix-wrapper-focused': {
          backgroundColor: theme.color.semantic.background.neutral.secondary,
          borderColor: theme.color.semantic.brand.default.moderate,
          boxShadow: 'none !important',
        },
      },
      '&.ant-input-sm': {
        height: '24px',
        padding: `${theme.spacing[0]} ${theme.spacing[3]} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
      '&.ant-input-affix-wrapper-sm': {
        height: '24px',
        padding: `${theme.spacing[0]} ${theme.spacing[3]} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
      '&.ant-input-lg': {
        height: '40px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
        fontSize: theme.typography.fontSize.md,
      },
      '&.ant-input-affix-wrapper-lg': {
        height: '40px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
        fontSize: theme.typography.fontSize.md,
      },
      '&.ant-input-group-wrapper': {
        display: 'inline-block',
        width: '100%',
        padding: `${theme.spacing[0]} !important`,
      },
      '&.ant-input-group-wrapper .ant-input-group': {
        display: 'flex',
        width: '100%',
        alignItems: 'stretch',
      },
      '& .ant-input-group .ant-input': {
        flex: 1,
        height: '32px',
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        border: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
        padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.regular,
        color: theme.color.semantic.text.primary,
        borderRadius: theme.radius.none,
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
        '&:hover': {
          borderColor: theme.color.semantic.brand.default.soft,
        },
        '&:focus': {
          borderColor: theme.color.semantic.brand.default.moderate,
        },
        '&:first-child': {
          borderTopLeftRadius: theme.radius.sm,
          borderBottomLeftRadius: theme.radius.sm,
        },
        '&:last-child': {
          borderTopRightRadius: theme.radius.sm,
          borderBottomRightRadius: theme.radius.sm,
        },
      },
      '& .ant-input-group-addon': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '32px',
        padding: `${theme.spacing[0]} ${theme.spacing[3]}`,
        color: theme.color.semantic.text.primary,
        fontWeight: theme.typography.fontWeight.regular,
        fontSize: theme.typography.fontSize.sm,
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        border: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
        whiteSpace: 'nowrap',
        minWidth: 'fit-content',
        '& .anticon': {
          color: theme.color.semantic.text.primary,
          fontSize: theme.typography.fontSize.md,
        },
        '&:first-child': {
          borderTopLeftRadius: theme.radius.sm,
          borderBottomLeftRadius: theme.radius.sm,
          borderRight: 'none',
          backgroundColor: theme.color.semantic.background.neutral.subtle,
        },
        '&:last-child': {
          borderTopRightRadius: theme.radius.sm,
          borderBottomRightRadius: theme.radius.sm,
          borderLeft: 'none',
          backgroundColor: theme.color.semantic.background.neutral.subtle,
        },
      },
      '&.ant-input-group-wrapper-sm .ant-input-group .ant-input': {
        height: '24px',
        padding: `${theme.spacing[0]} ${theme.spacing[3]} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
      '&.ant-input-group-wrapper-sm .ant-input-group-addon': {
        height: '24px',
        padding: `${theme.spacing[0]} ${theme.spacing[2]} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
      '&.ant-input-group-wrapper-lg .ant-input-group .ant-input': {
        height: '40px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
        fontSize: theme.typography.fontSize.md,
      },
      '&.ant-input-group-wrapper-lg .ant-input-group-addon': {
        height: '40px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
        fontSize: theme.typography.fontSize.md,
      },
      '& .ant-input-group .ant-input:disabled': {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
        opacity: `${theme.opacity[70]} !important`,
        color: theme.color.semantic.text.soft,
        cursor: 'not-allowed',
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
      },
      '& .ant-input-group-addon:has(~ .ant-input:disabled), & .ant-input-group .ant-input:disabled ~ .ant-input-group-addon': {
        opacity: `${theme.opacity[70]} !important`,
        color: theme.color.semantic.text.soft,
        cursor: 'not-allowed',
        '& .anticon': {
          color: theme.color.semantic.text.soft,
        },
      },
    };
  };

  const createSecondaryInputStyles = () => {
    return {
      backgroundColor: theme.color.semantic.brand.default.muted,
      color: `${theme.color.semantic.text.primary} !important`,
      borderColor: theme.color.semantic.brand.default.subtle,
      borderWidth: theme.border.width.sm,
      borderRadius: theme.radius.sm,
      fontWeight: theme.typography.fontWeight.regular,
      height: '32px',
      lineHeight: theme.typography.lineHeight.normal,
      padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
      fontSize: theme.typography.fontSize.sm,
      boxShadow: 'none !important',
      '& input': {
        backgroundColor: 'transparent !important',
        border: 'none',
        outline: 'none',
        boxShadow: 'none !important',
        fontSize: theme.typography.fontSize.sm,
        color: `${theme.color.semantic.text.primary} !important`,
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
      },
      '& textarea': {
        backgroundColor: 'transparent !important',
        border: 'none',
        outline: 'none',
        boxShadow: 'none !important',
        color: `${theme.color.semantic.text.primary} !important`,
      },
      '&::placeholder': {
        color: theme.color.semantic.text.soft,
      },
      '&:hover': {
        backgroundColor: theme.color.semantic.brand.default.light,
        borderColor: theme.color.semantic.brand.default.subtle,
        boxShadow: 'none !important',
      },
      '&:focus, &:active': {
        backgroundColor: theme.color.semantic.brand.default.soft,
        borderColor: theme.color.semantic.brand.default.subtle,
        boxShadow: 'none !important',
      },
      '&:disabled': {
        backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        opacity: `${theme.opacity[70]} !important`,
        color: `${theme.color.semantic.text.soft} !important`,
        borderColor: `${theme.color.semantic.border.primary} !important`,
        cursor: 'not-allowed !important',
        boxShadow: 'none !important',
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
        '&:hover, &:focus, &:active': {
          backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
          borderColor: `${theme.color.semantic.border.primary} !important`,
          boxShadow: 'none !important',
        },
      },
      '&.input-status-error': {
        borderColor: theme.color.semantic.brand.danger.light,
        '&:focus': {
          borderColor: theme.color.semantic.brand.danger.light,
        },
      },
      '&.input-status-warning': {
        borderColor: theme.color.semantic.accent.orange.strong,
        '&:focus': {
          borderColor: theme.color.semantic.accent.orange.strong,
        },
      },
      '& .ant-input-prefix, & .ant-input-suffix': {
        color: theme.color.semantic.text.primary,
        '& .anticon': {
          color: theme.color.semantic.text.primary,
        },
      },
      '&:disabled .ant-input-prefix, &:disabled .ant-input-suffix': {
        color: theme.color.semantic.text.tertiary,
        '& .anticon': {
          color: theme.color.semantic.text.tertiary,
        },
      },
      '&.ant-input-affix-wrapper': {
        height: '32px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
        backgroundColor: theme.color.semantic.brand.default.muted,
        borderColor: theme.color.semantic.brand.default.subtle,
        boxShadow: 'none !important',
        '&:hover': {
          backgroundColor: theme.color.semantic.brand.default.light,
          borderColor: theme.color.semantic.brand.default.subtle,
          boxShadow: 'none !important',
        },
        '&:focus, &.ant-input-affix-wrapper-focused': {
          backgroundColor: theme.color.semantic.brand.default.soft,
          borderColor: theme.color.semantic.brand.default.subtle,
          boxShadow: 'none !important',
        },
      },
      '&.ant-input-sm': {
        height: '24px',
        padding: `${theme.spacing[0]} ${theme.spacing[3]} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
      '&.ant-input-affix-wrapper-sm': {
        height: '24px',
        padding: `${theme.spacing[0]} ${theme.spacing[3]} !important`,
        fontSize: theme.typography.fontSize.sm,
        '&:hover, &:focus, &:active': {
          borderColor: `${theme.color.semantic.brand.default.subtle} !important`,
        },
      },
      '&.ant-input-lg': {
        height: '40px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
        fontSize: theme.typography.fontSize.md,
      },
      '&.ant-input-affix-wrapper-lg': {
        height: '40px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
        fontSize: theme.typography.fontSize.md,
        '&:hover, &:focus, &:active': {
          borderColor: `${theme.color.semantic.brand.default.subtle} !important`,
        },
      },
      '&.ant-input-group-wrapper': {
        display: 'inline-block',
        width: '100%',
        padding: `${theme.spacing[0]} !important`,
      },
      '&.ant-input-group-wrapper .ant-input-group': {
        display: 'flex',
        width: '100%',
        alignItems: 'stretch',
      },
      '& .ant-input-group .ant-input': {
        flex: 1,
        height: '32px',
        backgroundColor: theme.color.semantic.brand.default.muted,
        border: `${theme.border.width.sm} solid ${theme.color.semantic.brand.default.subtle}`,
        padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.regular,
        color: theme.color.semantic.text.primary,
        borderRadius: theme.radius.none,
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
        '&:hover': {
          backgroundColor: theme.color.semantic.brand.default.light,
          borderColor: theme.color.semantic.brand.default.subtle,
        },
        '&:focus': {
          backgroundColor: theme.color.semantic.brand.default.soft,
          borderColor: theme.color.semantic.brand.default.subtle,
        },
        '&:first-child': {
          borderTopLeftRadius: theme.radius.sm,
          borderBottomLeftRadius: theme.radius.sm,
        },
        '&:last-child': {
          borderTopRightRadius: theme.radius.sm,
          borderBottomRightRadius: theme.radius.sm,
        },
      },
      '& .ant-input-group-addon': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '32px',
        padding: `${theme.spacing[0]} ${theme.spacing[3]}`,
        color: theme.color.semantic.text.primary,
        fontWeight: theme.typography.fontWeight.regular,
        fontSize: theme.typography.fontSize.sm,
        backgroundColor: theme.color.semantic.brand.default.muted,
        border: `${theme.border.width.sm} solid ${theme.color.semantic.brand.default.subtle}`,
        whiteSpace: 'nowrap',
        minWidth: 'fit-content',
        '& .anticon': {
          color: theme.color.semantic.text.primary,
          fontSize: theme.typography.fontSize.md,
        },
        '&:first-child': {
          borderTopLeftRadius: theme.radius.sm,
          borderBottomLeftRadius: theme.radius.sm,
          borderRight: 'none',
          backgroundColor: theme.color.semantic.brand.default.light,
        },
        '&:last-child': {
          borderTopRightRadius: theme.radius.sm,
          borderBottomRightRadius: theme.radius.sm,
          borderLeft: 'none',
          backgroundColor: theme.color.semantic.brand.default.light,
        },
      },
      '&.ant-input-group-wrapper-sm .ant-input-group .ant-input': {
        height: '24px',
        padding: `${theme.spacing[0]} ${theme.spacing[3]} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
      '&.ant-input-group-wrapper-sm .ant-input-group-addon': {
        height: '24px',
        padding: `${theme.spacing[0]} ${theme.spacing[2]} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
      '&.ant-input-group-wrapper-lg .ant-input-group .ant-input': {
        height: '40px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
        fontSize: theme.typography.fontSize.md,
      },
      '&.ant-input-group-wrapper-lg .ant-input-group-addon': {
        height: '40px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
        fontSize: theme.typography.fontSize.md,
      },
      '& .ant-input-group .ant-input:disabled': {
        backgroundColor: theme.color.semantic.background.neutral.soft,
        opacity: `${theme.opacity[70]} !important`,
        color: theme.color.semantic.text.tertiary,
        cursor: 'not-allowed',
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
      },
      '& .ant-input-group-addon:has(~ .ant-input:disabled), & .ant-input-group .ant-input:disabled ~ .ant-input-group-addon': {
        opacity: `${theme.opacity[70]} !important`,
        color: theme.color.semantic.text.tertiary,
        cursor: 'not-allowed',
        '& .anticon': {
          color: theme.color.semantic.text.tertiary,
        },
      },
    };
  };

  return {
    defaultInput: createDefaultInputStyles(),
    secondaryInput: createSecondaryInputStyles(),

    defaultTextArea: {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      color: `${theme.color.semantic.text.primary} !important`,
      borderColor: theme.color.semantic.border.primary,
      borderWidth: theme.border.width.sm,
      borderStyle: 'solid',
      borderRadius: theme.radius.sm,
      fontWeight: theme.typography.fontWeight.regular,
      resize: 'vertical',
      padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
      fontSize: theme.typography.fontSize.sm,
      lineHeight: theme.typography.lineHeight.normal,
      boxShadow: 'none !important',
      '&.ant-input-textarea': {
        padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
      },
      '& textarea': {
        backgroundColor: 'transparent !important',
        border: 'none !important',
        outline: 'none !important',
        boxShadow: 'none !important',
        color: `${theme.color.semantic.text.primary} !important`,
        width: '100% !important',
        padding: `${theme.spacing[0]} !important`,
        fontSize: theme.typography.fontSize.sm,
        lineHeight: theme.typography.lineHeight.normal,
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
      },
      '&::placeholder': {
        color: theme.color.semantic.text.soft,
      },
      '&:hover': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.brand.default.soft,
        boxShadow: 'none !important',
      },
      '&:focus, &:active, &.ant-input-focused, &.ant-input-textarea-focused': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.brand.default.moderate,
        boxShadow: 'none !important',
      },
      '&:disabled': {
        backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        opacity: `${theme.opacity[70]} !important`,
        color: `${theme.color.semantic.text.soft} !important`,
        borderColor: `${theme.color.semantic.border.primary} !important`,
        cursor: 'not-allowed !important',
        boxShadow: 'none !important',
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
        '&:hover, &:focus, &:active': {
          backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
          borderColor: `${theme.color.semantic.border.primary} !important`,
          boxShadow: 'none !important',
        },
      },
      '&.input-status-error': {
        borderColor: theme.color.semantic.brand.danger.light,
        '&:focus': {
          borderColor: theme.color.semantic.brand.danger.light,
        },
      },
      '&.input-status-warning': {
        borderColor: theme.color.semantic.accent.orange.strong,
        '&:focus': {
          borderColor: theme.color.semantic.accent.orange.strong,
        },
      },
      '&.ant-input-textarea-show-count': {
        '&::after': {
          color: theme.color.semantic.text.soft,
          fontSize: theme.typography.fontSize.xs,
        },
      },
      '& .ant-input-data-count': {
        color: theme.color.semantic.text.soft,
        fontSize: theme.typography.fontSize.xs,
        backgroundColor: 'transparent',
      },
      '&.ant-input-affix-wrapper-textarea-with-clear-btn': {
        padding: `${theme.spacing[1]} ${theme.spacing[4]} !important`,
        display: 'flex',
        alignItems: 'flex-start',
        '& textarea': {
          padding: `${theme.spacing[0]} !important`,
          flex: 1,
        },
        '& .ant-input-clear-icon': {
          marginTop: theme.spacing[1],
        },
      },
    },
    secondaryTextArea: {
      backgroundColor: theme.color.semantic.brand.default.muted,
      color: `${theme.color.semantic.text.primary} !important`,
      borderColor: theme.color.semantic.brand.default.subtle,
      borderWidth: theme.border.width.sm,
      borderStyle: 'solid',
      borderRadius: theme.radius.sm,
      fontWeight: theme.typography.fontWeight.regular,
      resize: 'vertical',
      padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
      fontSize: theme.typography.fontSize.sm,
      lineHeight: theme.typography.lineHeight.normal,
      boxShadow: 'none !important',
      '&.ant-input-textarea': {
        padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
      },
      '& textarea': {
        backgroundColor: 'transparent !important',
        border: 'none !important',
        outline: 'none !important',
        boxShadow: 'none !important',
        color: `${theme.color.semantic.text.primary} !important`,
        width: '100% !important',
        padding: `${theme.spacing[0]} !important`,
        fontSize: theme.typography.fontSize.sm,
        lineHeight: theme.typography.lineHeight.normal,
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
      },
      '&::placeholder': {
        color: theme.color.semantic.text.soft,
      },
      '&:hover': {
        backgroundColor: theme.color.semantic.brand.default.light,
        borderColor: theme.color.semantic.brand.default.subtle,
        boxShadow: 'none !important',
      },
      '&:focus, &:active, &.ant-input-focused, &.ant-input-textarea-focused': {
        backgroundColor: theme.color.semantic.brand.default.soft,
        borderColor: theme.color.semantic.brand.default.subtle,
        boxShadow: 'none !important',
      },
      '&:disabled': {
        backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        opacity: `${theme.opacity[70]} !important`,
        color: `${theme.color.semantic.text.soft} !important`,
        borderColor: `${theme.color.semantic.border.primary} !important`,
        cursor: 'not-allowed !important',
        boxShadow: 'none !important',
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
        '&:hover, &:focus, &:active': {
          backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
          borderColor: `${theme.color.semantic.border.primary} !important`,
          boxShadow: 'none !important',
        },
      },
      '&.input-status-error': {
        borderColor: theme.color.semantic.brand.danger.light,
        '&:focus': {
          borderColor: theme.color.semantic.brand.danger.light,
        },
      },
      '&.input-status-warning': {
        borderColor: theme.color.semantic.accent.orange.strong,
        '&:focus': {
          borderColor: theme.color.semantic.accent.orange.strong,
        },
      },
      '&.ant-input-textarea-show-count': {
        '&::after': {
          color: theme.color.semantic.text.soft,
          fontSize: theme.typography.fontSize.xs,
        },
      },
      '& .ant-input-data-count': {
        color: theme.color.semantic.text.soft,
        fontSize: theme.typography.fontSize.xs,
        backgroundColor: 'transparent',
      },
    },

    defaultPassword: {
      ...createDefaultInputStyles(),
      '&.ant-input-affix-wrapper': {
        height: '32px',
        boxShadow: 'none !important',

        '&:hover': {
          backgroundColor: theme.color.semantic.background.neutral.secondary,
          borderColor: theme.color.semantic.brand.default.soft,
          boxShadow: 'none !important',
        },
        '&:focus, &.ant-input-affix-wrapper-focused': {
          backgroundColor: theme.color.semantic.background.neutral.secondary,
          borderColor: theme.color.semantic.brand.default.moderate,
          boxShadow: 'none !important',
        },
      },
      '&.ant-input-affix-wrapper-disabled': {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
        opacity: `${theme.opacity[70]} !important`,
        color: theme.color.semantic.text.soft,
        borderColor: theme.color.semantic.border.primary,
        cursor: 'not-allowed',
        '&:hover': {
          backgroundColor: theme.color.semantic.background.neutral.subtle,
          opacity: `${theme.opacity[70]} !important`,
          borderColor: theme.color.semantic.border.primary,
        },
        '& input': {
          cursor: 'not-allowed',
        },
        '& .ant-input-suffix .anticon': {
          color: theme.color.semantic.text.soft,
        },
      },
    },
    secondaryPassword: {
      ...createSecondaryInputStyles(),
      '&.ant-input-affix-wrapper': {
        height: '32px',
        boxShadow: 'none !important',

        '&:hover': {
          backgroundColor: theme.color.semantic.brand.default.light,
          borderColor: theme.color.semantic.brand.default.subtle,
          boxShadow: 'none !important',
        },
        '&:focus, &.ant-input-affix-wrapper-focused': {
          backgroundColor: theme.color.semantic.brand.default.soft,
          borderColor: theme.color.semantic.brand.default.subtle,
          boxShadow: 'none !important',
        },
      },
      '&.ant-input-affix-wrapper-disabled': {
        backgroundColor: theme.color.semantic.background.neutral.soft,
        opacity: `${theme.opacity[70]} !important`,
        color: theme.color.semantic.text.tertiary,
        borderColor: theme.color.semantic.border.subtle,
        cursor: 'not-allowed',
        '&:hover': {
          backgroundColor: theme.color.semantic.background.neutral.soft,
          opacity: `${theme.opacity[70]} !important`,
          borderColor: theme.color.semantic.border.subtle,
        },
        '& input': {
          cursor: 'not-allowed',
        },
        '& .ant-input-suffix .anticon': {
          color: theme.color.semantic.text.tertiary,
        },
      },
    },

    defaultSearch: {
      ...createDefaultInputStyles(),
      '&.ant-input-affix-wrapper': {
        height: '32px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.border.primary,
        boxShadow: 'none !important',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: theme.color.semantic.background.neutral.secondary,
          borderColor: theme.color.semantic.brand.default.soft,
          boxShadow: 'none !important',
        },
        '&:focus, &.ant-input-affix-wrapper-focused': {
          backgroundColor: theme.color.semantic.background.neutral.secondary,
          borderColor: theme.color.semantic.brand.default.moderate,
          boxShadow: 'none !important',
        },
      },
      '&.ant-input-affix-wrapper-sm': {
        height: '24px',
        padding: `${theme.spacing[0]} ${theme.spacing[3]} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
      '&.ant-input-affix-wrapper-lg': {
        height: '40px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
        fontSize: theme.typography.fontSize.md,
      },
      '&.ant-input-affix-wrapper-disabled': {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
        opacity: `${theme.opacity[70]} !important`,
        color: theme.color.semantic.text.soft,
        borderColor: theme.color.semantic.border.primary,
        cursor: 'not-allowed',
        '&:hover': {
          backgroundColor: theme.color.semantic.background.neutral.subtle,
          borderColor: theme.color.semantic.border.primary,
        },
        '& input': {
          cursor: 'not-allowed',
        },
        '& .ant-input-suffix .anticon': {
          color: theme.color.semantic.text.soft,
        },
      },
      '& .ant-input-search-with-button .ant-input-wrapper': {
        display: 'flex !important',
        width: '100%',
      },
      '& .ant-input-wrapper.ant-input-group': {
        display: 'flex',
        '& .ant-input-affix-wrapper': {
          backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
          border: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary} !important`,
          borderRight: 'none !important',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
          boxShadow: 'none !important',
          borderTopLeftRadius: `${theme.radius.sm} !important`,
          borderBottomLeftRadius: `${theme.radius.sm} !important`,
          borderTopRightRadius: `${theme.radius.none} !important`,
          borderBottomRightRadius: `${theme.radius.none} !important`,
          flex: 1,
          '& input': {
            backgroundColor: 'transparent !important',
            padding: `${theme.spacing[0]} !important`,
            border: 'none !important',
          },
          '&:hover': {
            backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
            borderColor: `${theme.color.semantic.brand.default.soft} !important`,
            borderRight: 'none !important',
          },
          '&:focus, &.ant-input-affix-wrapper-focused': {
            backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
            borderColor: `${theme.color.semantic.brand.default.moderate} !important`,
            borderRight: 'none !important',
          },
        },
      },
      '&.ant-input-search-sm .ant-input-wrapper.ant-input-group .ant-input-affix-wrapper': {
        height: '24px',
      },
      '&.ant-input-search-lg .ant-input-wrapper.ant-input-group .ant-input-affix-wrapper': {
        height: '40px',
      },
      '& .ant-input-group > .ant-input-group-addon:last-child': {
        backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
        borderTop: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary} !important`,
        borderRight: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary} !important`,
        borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary} !important`,
        borderLeft: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary} !important`,
        padding: `${theme.spacing[0]} !important`,
        borderTopRightRadius: `${theme.radius.sm} !important`,
        borderBottomRightRadius: `${theme.radius.sm} !important`,
        borderTopLeftRadius: `${theme.radius.none} !important`,
        borderBottomLeftRadius: `${theme.radius.none} !important`,
        height: '32px !important',
        display: 'flex !important',
        alignItems: 'center !important',
        overflow: 'hidden',
        '& .ant-input-search-button': {
          backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
          border: 'none !important',
          color: `${theme.color.semantic.text.primary} !important`,
          height: '100% !important',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: `${theme.spacing[0]} ${theme.spacing[3]}`,
          borderRadius: `${theme.radius.none} !important`,
          '&:hover': {
            backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
            color: `${theme.color.semantic.text.primary} !important`,
          },
          '&:focus': {
            backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
            color: `${theme.color.semantic.text.primary} !important`,
          },
          '& .anticon': {
            color: `${theme.color.semantic.text.primary} !important`,
          },
          '&:disabled': {
            backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
            opacity: `${theme.opacity[70]} !important`,
            color: `${theme.color.semantic.text.soft} !important`,
            cursor: 'not-allowed',
            '& .anticon': {
              color: `${theme.color.semantic.text.soft} !important`,
            },
          },
        },
      },
      '&.ant-input-search-sm .ant-input-group-addon': {
        height: '24px !important',
      },
      '&.ant-input-search-lg .ant-input-group-addon': {
        height: '40px !important',
      },
      '& .ant-input-group:hover > .ant-input-group-addon:last-child': {
        borderTopColor: `${theme.color.semantic.brand.default.soft} !important`,
        borderRightColor: `${theme.color.semantic.brand.default.soft} !important`,
        borderBottomColor: `${theme.color.semantic.brand.default.soft} !important`,
        borderLeftColor: `${theme.color.semantic.brand.default.soft} !important`,
      },
      '& .ant-input-wrapper.ant-input-group:has(.ant-input-affix-wrapper-focused) > .ant-input-group-addon:last-child': {
        borderTopColor: `${theme.color.semantic.brand.default.moderate} !important`,
        borderRightColor: `${theme.color.semantic.brand.default.moderate} !important`,
        borderBottomColor: `${theme.color.semantic.brand.default.moderate} !important`,
        borderLeftColor: `${theme.color.semantic.brand.default.moderate} !important`,
      },
    },
    secondarySearch: {
      ...createSecondaryInputStyles(),
      '&.ant-input-affix-wrapper': {
        height: '32px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
        backgroundColor: theme.color.semantic.brand.default.muted,
        borderColor: theme.color.semantic.brand.default.subtle,
        boxShadow: 'none !important',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: theme.color.semantic.brand.default.light,
          borderColor: theme.color.semantic.brand.default.subtle,
          boxShadow: 'none !important',
        },
        '&:focus, &.ant-input-affix-wrapper-focused': {
          backgroundColor: theme.color.semantic.brand.default.soft,
          borderColor: theme.color.semantic.brand.default.subtle,
          boxShadow: 'none !important',
        },
      },
      '&.ant-input-affix-wrapper-sm': {
        height: '24px',
        padding: `${theme.spacing[0]} ${theme.spacing[3]} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
      '&.ant-input-affix-wrapper-lg': {
        height: '40px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
        fontSize: theme.typography.fontSize.md,
      },
      '&.ant-input-affix-wrapper-disabled': {
        backgroundColor: theme.color.semantic.background.neutral.soft,
        opacity: `${theme.opacity[70]} !important`,
        color: theme.color.semantic.text.tertiary,
        borderColor: theme.color.semantic.border.subtle,
        cursor: 'not-allowed',
        '&:hover': {
          backgroundColor: theme.color.semantic.background.neutral.soft,
          borderColor: theme.color.semantic.border.subtle,
        },
        '& input': {
          cursor: 'not-allowed',
        },
        '& .ant-input-suffix .anticon': {
          color: theme.color.semantic.text.tertiary,
        },
      },
      '& .ant-input-search-with-button .ant-input-wrapper': {
        display: 'flex !important',
        width: '100%',
      },
      '& .ant-input-wrapper.ant-input-group': {
        display: 'flex',
        '& .ant-input-affix-wrapper': {
          backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
          border: `${theme.border.width.sm} solid ${theme.color.semantic.brand.default.subtle} !important`,
          borderRight: 'none !important',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
          boxShadow: 'none !important',
          borderTopLeftRadius: `${theme.radius.sm} !important`,
          borderBottomLeftRadius: `${theme.radius.sm} !important`,
          borderTopRightRadius: `${theme.radius.none} !important`,
          borderBottomRightRadius: `${theme.radius.none} !important`,
          flex: 1,
          '& input': {
            backgroundColor: 'transparent !important',
            padding: `${theme.spacing[0]} !important`,
            border: 'none !important',
          },
          '&:hover': {
            backgroundColor: `${theme.color.semantic.brand.default.light} !important`,
            borderColor: `${theme.color.semantic.brand.default.subtle} !important`,
            borderRight: 'none !important',
          },
          '&:focus, &.ant-input-affix-wrapper-focused': {
            backgroundColor: `${theme.color.semantic.brand.default.soft} !important`,
            borderColor: `${theme.color.semantic.brand.default.subtle} !important`,
            borderRight: 'none !important',
          },
        },
      },
      '&.ant-input-search-sm .ant-input-wrapper.ant-input-group .ant-input-affix-wrapper': {
        height: '24px',
      },
      '&.ant-input-search-lg .ant-input-wrapper.ant-input-group .ant-input-affix-wrapper': {
        height: '40px',
      },
      '& .ant-input-group > .ant-input-group-addon:last-child': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
        borderTop: `${theme.border.width.sm} solid ${theme.color.semantic.brand.default.subtle} !important`,
        borderRight: `${theme.border.width.sm} solid ${theme.color.semantic.brand.default.subtle} !important`,
        borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.brand.default.subtle} !important`,
        borderLeft: `${theme.border.width.sm} solid ${theme.color.semantic.brand.default.subtle} !important`,
        padding: `${theme.spacing[0]} !important`,
        borderTopRightRadius: `${theme.radius.sm} !important`,
        borderBottomRightRadius: `${theme.radius.sm} !important`,
        borderTopLeftRadius: `${theme.radius.none} !important`,
        borderBottomLeftRadius: `${theme.radius.none} !important`,
        height: '32px !important',
        display: 'flex !important',
        alignItems: 'center !important',
        overflow: 'hidden',
        '& .ant-input-search-button': {
          backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
          border: 'none !important',
          color: `${theme.color.semantic.text.primary} !important`,
          height: '100% !important',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: `${theme.spacing[0]} ${theme.spacing[3]}`,
          borderRadius: `${theme.radius.none} !important`,
          '&:hover': {
            backgroundColor: `${theme.color.semantic.brand.default.light} !important`,
            color: `${theme.color.semantic.text.primary} !important`,
          },
          '&:focus': {
            backgroundColor: `${theme.color.semantic.brand.default.soft} !important`,
            color: `${theme.color.semantic.text.primary} !important`,
          },
          '& .anticon': {
            color: `${theme.color.semantic.text.primary} !important`,
          },
          '&:disabled': {
            backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
            opacity: `${theme.opacity[70]} !important`,
            color: `${theme.color.semantic.text.tertiary} !important`,
            cursor: 'not-allowed',
            '& .anticon': {
              color: `${theme.color.semantic.text.tertiary} !important`,
            },
          },
        },
      },
      '&.ant-input-search-sm .ant-input-group-addon': {
        height: '24px !important',
      },
      '&.ant-input-search-lg .ant-input-group-addon': {
        height: '40px !important',
      },
      '& .ant-input-group:hover > .ant-input-group-addon:last-child': {
        borderTopColor: `${theme.color.semantic.brand.default.subtle} !important`,
        borderRightColor: `${theme.color.semantic.brand.default.subtle} !important`,
        borderBottomColor: `${theme.color.semantic.brand.default.subtle} !important`,
        borderLeftColor: `${theme.color.semantic.brand.default.subtle} !important`,
      },
      '& .ant-input-wrapper.ant-input-group:has(.ant-input-affix-wrapper-focused) > .ant-input-group-addon:last-child': {
        borderTopColor: `${theme.color.semantic.brand.default.subtle} !important`,
        borderRightColor: `${theme.color.semantic.brand.default.subtle} !important`,
        borderBottomColor: `${theme.color.semantic.brand.default.subtle} !important`,
        borderLeftColor: `${theme.color.semantic.brand.default.subtle} !important`,
      },
    },

    inverseSearch: {
      '&.ant-input-search': {
        backgroundColor: 'transparent !important',
        border: 'none !important',
        boxShadow: `${theme.shadow.none} !important`,
        padding: `${theme.spacing[0]} !important`,
      },
      '&.ant-input-search .ant-input-wrapper.ant-input-group .ant-input-affix-wrapper': {
        backgroundColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[5]} !important`,
        border: 'none !important',
        boxShadow: `${theme.shadow.none} !important`,
        borderTopLeftRadius: `${theme.radius.sm} !important`,
        borderBottomLeftRadius: `${theme.radius.sm} !important`,
        borderTopRightRadius: `${theme.radius.none} !important`,
        borderBottomRightRadius: `${theme.radius.none} !important`,
        '& input': {
          backgroundColor: 'transparent !important',
          color: `${theme.color.semantic.text.inverse} !important`,
          border: 'none !important',
          '&::placeholder': {
            color: `${theme.color.semantic.text.inverse}${theme.alpha[60]} !important`,
          },
        },
        '& .anticon': {
          color: `${theme.color.semantic.text.inverse} !important`,
        },
        '&:hover': {
          backgroundColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[20]} !important`,
          borderColor: 'transparent !important',
        },
        '&:focus, &.ant-input-affix-wrapper-focused': {
          backgroundColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[20]} !important`,
          borderColor: 'transparent !important',
          boxShadow: `${theme.shadow.none} !important`,
        },
      },
      '&.ant-input-search .ant-input-group > .ant-input-group-addon:last-child': {
        backgroundColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[5]} !important`,
        border: 'none !important',
        borderTopRightRadius: `${theme.radius.sm} !important`,
        borderBottomRightRadius: `${theme.radius.sm} !important`,
        borderTopLeftRadius: `${theme.radius.none} !important`,
        borderBottomLeftRadius: `${theme.radius.none} !important`,
        '& .ant-input-search-button': {
          backgroundColor: 'transparent !important',
          border: 'none !important',
          color: `${theme.color.semantic.text.inverse} !important`,
          height: '100% !important',
          padding: `${theme.spacing[0]} ${theme.spacing[2]} !important`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: `${theme.radius.none} !important`,
          boxShadow: `${theme.shadow.none} !important`,
          '&:hover': {
            backgroundColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[10]} !important`,
            color: `${theme.color.semantic.text.inverse} !important`,
          },
          '&:focus': {
            backgroundColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[10]} !important`,
            color: `${theme.color.semantic.text.inverse} !important`,
            outline: 'none !important',
          },
          '& .anticon': {
            color: `${theme.color.semantic.text.inverse} !important`,
          },
          '&:disabled': {
            backgroundColor: 'transparent !important',
            opacity: `${theme.opacity[70]} !important`,
            color: `${theme.color.semantic.text.inverse}${theme.alpha[60]} !important`,
            cursor: 'not-allowed',
            '& .anticon': {
              color: `${theme.color.semantic.text.inverse}${theme.alpha[60]} !important`,
            },
          },
        },
      },
    },
  };
});

const useInputStyles = ({
  variant = 'default',
  status,
  className = '',
  type = 'input',
  disabled,
}: InputStylesParams): string => {
  const classes = useStyles();

  const getVariantClass = () => {
    const styleMap = {
      input: variant === 'secondary' ? classes.secondaryInput : classes.defaultInput,
      textArea: variant === 'secondary' ? classes.secondaryTextArea : classes.defaultTextArea,
      password: variant === 'secondary' ? classes.secondaryPassword : classes.defaultPassword,
      search: variant === 'secondary'
        ? classes.secondarySearch
        : variant === 'inverse'
          ? classes.inverseSearch
          : classes.defaultSearch,
    };
    return styleMap[type];
  };

  const variantClass = getVariantClass();
  const statusClass = status ? `input-status-${status}` : '';
  const disabledClass = type === 'search' && disabled ? 'ant-input-search-disabled' : '';

  return `${variantClass} ${statusClass} ${disabledClass} ${className}`.trim();
};

const InputComponent: React.FC<InputProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  ...rest
}) => {
  const combinedClassName = useInputStyles({ variant, status, className, type: 'input' });

  return (
    <AntInput
      size={size}
      {...rest}
      className={combinedClassName}
    />
  );
};

export const Input = InputComponent as typeof InputComponent & {
  TextArea: typeof InputTextArea;
  Password: typeof InputPassword;
  Search: typeof InputSearch;
  Group: typeof AntInput.Group;
};

export interface TextAreaProps extends React.ComponentProps<typeof AntInput.TextArea> {
  variant?: InputVariant;
  className?: string;
  status?: 'error' | 'warning';
}

export const InputTextArea: React.FC<TextAreaProps> = ({
  variant = 'default',
  className = '',
  status,
  ...rest
}) => {
  const combinedClassName = useInputStyles({ variant, status, className, type: 'textArea' });

  return (
    <TextArea
      {...rest}
      className={combinedClassName}
    />
  );
};

export interface PasswordProps extends React.ComponentProps<typeof AntInput.Password> {
  variant?: InputVariant;
  className?: string;
  status?: 'error' | 'warning';
}

export const InputPassword: React.FC<PasswordProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  ...rest
}) => {
  const combinedClassName = useInputStyles({ variant, status, className, type: 'password' });

  return (
    <Password
      size={size}
      {...rest}
      className={combinedClassName}
    />
  );
};

export interface SearchProps extends React.ComponentProps<typeof AntInput.Search> {
  variant?: InputVariant;
  className?: string;
  status?: 'error' | 'warning';
}

export const InputSearch: React.FC<SearchProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  disabled,
  ...rest
}) => {
  const combinedClassName = useInputStyles({ variant, status, className, type: 'search', disabled });

  return (
    <Search
      size={size}
      disabled={disabled}
      {...rest}
      className={combinedClassName}
    />
  );
};

Input.TextArea = InputTextArea;
Input.Password = InputPassword;
Input.Search = InputSearch;
Input.Group = AntInput.Group;

export default Input;